import { useFrame, useThree } from "@react-three/fiber";
import { useEffect } from "react";
import { useCameraStore } from "../../stores/camera";
import type { StickVector, ControlMode } from "./control-types";
import type { MarbleControls } from "./marble-controls";

type MarbleControlsWrapperProps = {
  move: StickVector;
  look: StickVector;
  mode: ControlMode;
  controls: MarbleControls;
  children?: React.ReactNode;
};

export const MarbleControlsWrapper = ({
  move,
  look,
  mode,
  controls,
  children,
}: MarbleControlsWrapperProps) => {
  const { camera, gl } = useThree();
  const setFov = useCameraStore((state) => state.setFov);
  const setTargetFov = useCameraStore((state) => state.setTargetFov);
  const setHasUserInteracted = useCameraStore(
    (state) => state.setHasUserInteracted
  );
  const fov = useCameraStore((state) => state.fov);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fov intentionally excluded to prevent feedback loop
  // TODO: looks like this should be removed, and anything else marble specific about the cameras. (ie. this should get set by server )
  useEffect(() => {
    if (controls && gl?.domElement) {
      controls.initialize(gl.domElement);
      controls.setCamera(camera);
      controls.setInitialFov(fov);
      controls.setFovTargetChangeCallback((targetFov) => {
        setTargetFov(targetFov);
        if (targetFov !== null) {
          setHasUserInteracted(true);
        }
      });
      return () => {
        controls.dispose();
      };
    }
  }, [controls, gl?.domElement, camera, setTargetFov, setHasUserInteracted]);

  useEffect(() => {
    controls.setControlMode(mode);
  }, [controls, mode]);

  useFrame(() => {
    controls.fpsControls.updateJoystickInput(move.x, move.y);
    controls.setLookJoystickInput(look.x, look.y);
    controls.update(camera);

    if (camera && "fov" in camera) {
      const cameraFov = (camera as { fov: number }).fov;
      if (typeof cameraFov === "number" && Number.isFinite(cameraFov)) {
        setFov(cameraFov);
      }
    }
  });

  return <>{children}</>;
};
